import React, { memo } from "react";
import Section from "./Section";
import SectionHeader from "./SectionHeader";
import Image from "next/image";
import airConditioner from "@/public/images/unit-details-icons/air-conditioner.svg";
import areaIcon from "@/public/images/unit-details-icons/area.svg";
import bathroom from "@/public/images/unit-details-icons/bathroom.svg";
import coffeeMaker from "@/public/images/unit-details-icons/coffee-maker.svg";
import doubleBed from "@/public/images/unit-details-icons/double-bed.svg";
import dryer from "@/public/images/unit-details-icons/dryer.svg";
import elevator from "@/public/images/unit-details-icons/elevator.svg";
import grillCorner from "@/public/images/unit-details-icons/grill-corner.svg";
import jacuzzi from "@/public/images/unit-details-icons/jacuzzi.svg";
import livingRoom from "@/public/images/unit-details-icons/living-room.svg";
import microwave from "@/public/images/unit-details-icons/microwave.svg";
import sauna from "@/public/images/unit-details-icons/sauna.svg";
import spa from "@/public/images/unit-details-icons/spa.svg";
import wifi from "@/public/images/unit-details-icons/wifi.svg";
import toys from "@/public/images/unit-details-icons/toys.svg";
import boardGames from "@/public/images/unit-details-icons/board_games.svg";
import shower from "@/public/images/unit-details-icons/shower.svg";
import bathTub from "@/public/images/unit-details-icons/bathTub.svg";
import refrigerator from "@/public/images/unit-details-icons/refrigerator.svg";
import iron from "@/public/images/unit-details-icons/iron.svg";
import bathrobe from "@/public/images/unit-details-icons/bathrobe.svg";
import shampoo from "@/public/images/unit-details-icons/shampoo.svg";
import soap from "@/public/images/unit-details-icons/soap.svg";
import napkins from "@/public/images/unit-details-icons/napkins.svg";

// Additional missing icon imports
import adultPool from "@/public/images/unit-details-icons/adult-pool.svg";
import childrenPool from "@/public/images/unit-details-icons/children-pool.svg";
import sharedPool from "@/public/images/unit-details-icons/shared-pool.svg";
import swimmingPool from "@/public/images/unit-details-icons/swimming-pool.svg";
import waterGames from "@/public/images/unit-details-icons/water-games.svg";
import gym from "@/public/images/unit-details-icons/gym.svg";
import parking from "@/public/images/unit-details-icons/parking.svg";
import balcony from "@/public/images/unit-details-icons/balcony.svg";
import garden from "@/public/images/unit-details-icons/garden.svg";
import outdoorSpace from "@/public/images/unit-details-icons/outdoor-space.svg";
import washer from "@/public/images/unit-details-icons/washer.svg";
import speakers from "@/public/images/unit-details-icons/speakers.svg";
import billiards from "@/public/images/unit-details-icons/billiards.svg";
import massageChair from "@/public/images/unit-details-icons/massage-chair.svg";
import sunbathingChair from "@/public/images/unit-details-icons/sunbathing-chair.svg";
import projector from "@/public/images/unit-details-icons/projector.svg";
import cinemaRoom from "@/public/images/unit-details-icons/cinema-room.svg";
import flatScreenTV from "@/public/images/unit-details-icons/flat-screen-tv.svg";
import bathroomHeater from "@/public/images/unit-details-icons/bathroom-heater.svg";
import towels from "@/public/images/unit-details-icons/towel-warmer.svg";
import hairDryer from "@/public/images/unit-details-icons/hair-dryer.svg";
import oven from "@/public/images/unit-details-icons/oven.svg";
import dishwasher from "@/public/images/unit-details-icons/dishwasher.svg";
import heating from "@/public/images/unit-details-icons/heating.svg";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components-shared/ui/card";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components-shared/ui/carousel";
import { StaticImport } from "next/dist/shared/lib/get-img-props";
import { dir } from "i18next";
import { Button } from "@/components-shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from "@/components-shared/ui/dialog";
import {
  AdvancedMarker,
  APIProvider,
  Map as GoogleMap,
  Pin,
} from "@vis.gl/react-google-maps";
import { useLocale, useTranslations } from "next-intl";

const UnitDetails = ({
  area,
  numOfBathrooms,
  numOfBedrooms,
  numOfLivingRooms,
  entertainment,
  noTitle,
  code,
  lat,
  lng,
}: {
  numOfBedrooms?: number;
  numOfBathrooms?: number;
  numOfLivingRooms?: number;
  area?: number;
  entertainment?: Record<string, boolean>[];
  noTitle?: boolean;
  code?: string;
  lat?: number;
  lng?: number;
}) => {
  const locale = useLocale();
  const t = useTranslations();
  const entertainmentMap = new Map(
    entertainment?.map((obj) => Object.entries(obj || {})[0])
  );

  return (
    <Section>
      {!noTitle && (
        <div className="flex justify-between  items-center gap-8">
          <div className="flex items-center gap-6 max-sm:justify-between">
            <SectionHeader>{t("unitDetails.title")}</SectionHeader>
            <p className="text-gray-light max-sm:hidden">
              {t("unitDetails.code", { code: code || "----" })}
            </p>
          </div>
          <UnitLocation {...{ lat, lng }} />
        </div>
      )}
      <Carousel
        opts={{
          direction: dir(locale as string),
          dragFree: true,
        }}
        className="flex items-center gap-5 w-full"
      >
        <CarouselPrevious className="size-6 relative rtl:rotate-180 p-0 border-none !bg-transparent [&>svg]:stroke-gray-dark [&>svg]:disabled:stroke-gray-400 " />
        <CarouselContent
          aria-expanded="true"
          className="sm:px-3 grow items-stretch *:basis-40 *:max-sm:basis-28 *:select-none"
        >
          <CarouselItem className="max-sm:pr-0">
            <UnitComponent
              title={t("unitComponents.bedroomsTitle")}
              src={doubleBed}
              count={numOfBedrooms || 0}
            />
          </CarouselItem>
          <CarouselItem>
            <UnitComponent
              title={t("unitComponents.bathroomsTitle")}
              src={bathroom}
              count={numOfBathrooms || 0}
            />
          </CarouselItem>
          <CarouselItem>
            <UnitComponent
              title={t("unitComponents.livingRoomsTitle")}
              src={livingRoom}
              count={numOfLivingRooms || 0}
            />
          </CarouselItem>
          <CarouselItem>
            <UnitComponent
              title={t("unitComponents.areaTitle")}
              src={areaIcon}
              count={area ? Number(area).toLocaleString("en-US") : 0}
              isArea
            />
          </CarouselItem>
          {[
            // General Facilities
            ["adultPool", adultPool],
            ["childrenPool", childrenPool],
            ["sharedPool", sharedPool],
            ["tieredPool", swimmingPool],
            ["waterGames", waterGames],
            ["gym", gym],
            ["elevator", elevator],
            ["parking", parking],
            ["grill", grillCorner],
            ["grillCorner", grillCorner],
            ["airConditioning", airConditioner],
            ["airConditioner", airConditioner],
            ["wifi", wifi],
            ["balcony", balcony],
            ["garden", garden],
            ["outdoorSpace", outdoorSpace],
            ["washer", washer],
            ["speakers", speakers],
            ["billiards", billiards],
            ["massageChair", massageChair],
            ["sunbathingChair", sunbathingChair],
            ["projector", projector],
            ["cinemaRoom", cinemaRoom],
            ["flatScreenTV", flatScreenTV],

            // Bathroom Facilities
            ["shower", shower],
            ["bathTub", bathTub],
            ["jacuzzi", jacuzzi],
            ["hotWater", bathroomHeater],
            ["bathroomSupplies", bathroom],
            ["towels", towels],
            ["hairDryer", hairDryer],

            // Kitchen Facilities
            ["refrigerator", refrigerator],
            ["microwave", microwave],
            ["coffeeMaker", coffeeMaker],
            ["oven", oven],
            ["dishwasher", dishwasher],

            // Additional Amenities
            ["sauna", sauna],
            ["spa", spa],
            ["toys", toys],
            ["intelGames", boardGames],
            ["iron", iron],
            ["dryer", dryer],
            ["bathrobe", bathrobe],
            ["shampoo", shampoo],
            ["soap", soap],
            ["napkins", napkins],
            ["heater", heating],
          ].map(
            ([key, src]) =>
              entertainmentMap?.get(key) && (
                <CarouselItem key={key}>
                  <UnitComponent title={t(`entertainment.${key}`)} src={src} />
                </CarouselItem>
              )
          )}
          {/* Display all entertainment items, even those without icons */}
          {entertainment?.map((entertainmentItem, index) => {
            const [key, value] = Object.entries(entertainmentItem || {})[0];
            // Skip if already displayed above (has an icon)
            const hasIcon = [
              "adultPool", "childrenPool", "sharedPool", "tieredPool", "waterGames", "gym", "elevator", "parking",
              "grill", "grillCorner", "airConditioning", "airConditioner", "wifi", "balcony", "garden",
              "outdoorSpace", "washer", "speakers", "billiards", "massageChair", "sunbathingChair",
              "projector", "cinemaRoom", "flatScreenTV", "shower", "bathTub", "jacuzzi", "hotWater",
              "bathroomSupplies", "towels", "hairDryer", "refrigerator", "microwave", "coffeeMaker",
              "oven", "dishwasher", "sauna", "spa", "toys", "intelGames", "iron", "dryer", "bathrobe",
              "shampoo", "soap", "napkins", "heater"
            ].includes(key);

            if (hasIcon || !value) return null;

            return (
              <CarouselItem key={`${key}-${index}`}>
                <UnitComponentNoIcon title={t(`entertainment.${key}`)} />
              </CarouselItem>
            );
          })}
        </CarouselContent>
        <CarouselNext className="size-6 rtl:rotate-180 p-0 border-none !bg-transparent [&>svg]:stroke-gray-dark [&>svg]:disabled:stroke-gray-400" />
      </Carousel>
    </Section>
  );
};

export default memo(UnitDetails);

type Props = {
  title: string;
  src: StaticImport | null;
  count?: number | string;
  isArea?: boolean;
};

const UnitComponent = ({ count, src, title, isArea }: Props) => {
  const locale = useLocale();
  return (
    <Card className="bg-green-background space-y-2 rounded-2xl py-2 px-3">
      <CardHeader>
        <CardTitle className="text-sm text-gray-light font-normal max-sm:text-xs">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-row-reverse items-center justify-between gap-1 sm:gap-3">
        <span className="text-gray-dark min-w-5 max-sm:text-sm">
          {count ?? ""}
          {isArea && (
            <>
              {locale === "ar" ? "م" : "m"}
              <sup>2</sup>
            </>
          )}
        </span>
        {src && <Image src={src} alt={title} className="size-8 sm:size-12" />}
      </CardContent>
    </Card>
  );
};

const UnitComponentNoIcon = ({ title }: { title: string }) => {
  return (
    <Card className="bg-green-background space-y-2 rounded-2xl py-2 px-3">
      <CardHeader>
        <CardTitle className="text-sm text-gray-light font-normal max-sm:text-xs">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-row-reverse items-center justify-between gap-1 sm:gap-3">
        <span className="text-gray-dark min-w-5 max-sm:text-sm">
          {/* Empty space where count would be */}
        </span>
        <div className="size-8 sm:size-12">
          {/* Empty space where icon would be - same size as icon */}
        </div>
      </CardContent>
    </Card>
  );
};

const UnitLocation = ({ lat, lng }: { lat?: number; lng?: number }) => {
  const t = useTranslations();

  return lat && lng ? (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          variant={"link"}
          className="!h-auto !p-0 underline text-gray-500 hover:text-slate-900"
        >
          {t("unitDetails.map")}
        </Button>
      </DialogTrigger>
      <DialogContent
        data-close-hidden
        className="max-w-3xl w-full h-[400px] p-1"
      >
        <DialogTitle className="hidden" />
        <DialogDescription className="hidden" />
        <div className="rounded-md overflow-hidden">
          <APIProvider
            apiKey={process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY as string}
          >
            <GoogleMap
              defaultCenter={{ lat, lng }}
              restriction={{
                latLngBounds: {
                  north: 32.1543,
                  south: 16.5,
                  west: 34.5,
                  east: 55.6667,
                },
                strictBounds: true,
              }}
              defaultZoom={10}
              mapId={process.env.NEXT_PUBLIC_GOOGLE_MAPS_ID}
            >
              <AdvancedMarker position={{ lat, lng }}>
                <Pin />
              </AdvancedMarker>
            </GoogleMap>
          </APIProvider>
        </div>
      </DialogContent>
    </Dialog>
  ) : null;
};
